import React from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import type { Locale } from '../../../bridge/protocol';
import type { Transaction } from '../types';
import {
  FINANCIAL_ICON_BG,
  MOBILE,
  NON_FINANCIAL_ACCENT,
  NON_FINANCIAL_ICON_BG,
  NON_FINANCIAL_ICON_CLIP,
  TX_FAILED_BADGE_BG,
  TX_FONT_CREDIT,
  TX_FONT_FAILED,
  TX_FONT_PENDING,
  TX_FONT_SETTLED,
  TX_ROW_DIVIDER,
  lastFour,
} from './styles';

/**
 * Recent-activity transaction row — bespoke styled-component faithful to
 * `reference/transaction-row.css` (PRD §5, Option A; DS `TableRow` rejected in
 * D-1). Row text colour is state-driven (SETTLED/PENDING/FAILED/non-financial),
 * the primary amount keeps its vertical space when hidden on non-financial
 * rows, and a present `url` makes the whole row a `navigate:native` hand-off
 * (PRD §8).
 */

type Tone = 'failed' | 'nonfin' | 'pending' | 'settled';

const toneOf = (tx: Transaction): Tone => {
  if (tx.status === 'FAILED') return 'failed';
  if (!tx.isFinancial) return 'nonfin';
  if (tx.status === 'PENDING') return 'pending';
  return 'settled';
};

const Root = styled.div<{ $tone: Tone; $strike: boolean; $last: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 0;
  border-bottom: ${({ $last }) => ($last ? 'none' : `1px solid ${TX_ROW_DIVIDER}`)};
  --font-size: 14px;
  --sub-font-size: 12px;
  color: ${({ $tone }) =>
    $tone === 'failed'
      ? TX_FONT_FAILED
      : $tone === 'nonfin'
        ? NON_FINANCIAL_ACCENT
        : $tone === 'pending'
          ? TX_FONT_PENDING
          : TX_FONT_SETTLED};

  @media (min-width: ${MOBILE}) {
    --font-size: 16px;
    --sub-font-size: 14px;
    gap: 16px;
  }

  ${({ $strike }) => ($strike ? '.amounts { text-decoration: line-through; }' : '')}
`;

const IconBox = styled.div<{ $nonFinancial: boolean }>`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9.6px;
  background: ${({ $nonFinancial }) => ($nonFinancial ? NON_FINANCIAL_ICON_BG : FINANCIAL_ICON_BG)};

  @media (min-width: ${MOBILE}) {
    border-radius: 12px;
  }
`;

const FinancialSymbol = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid #667085;
  border-radius: 50%;
  background: transparent;
`;

const NonFinancialSymbol = styled.span`
  width: 16px;
  height: 16px;
  background: ${NON_FINANCIAL_ACCENT};
  clip-path: ${NON_FINANCIAL_ICON_CLIP};
`;

const Details = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ellipsis = `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Vendor = styled.div`
  font-size: var(--font-size);
  font-weight: 600;
  line-height: 1.4;
  ${ellipsis}
`;

const CardLine = styled.div`
  font-size: var(--sub-font-size);
  font-weight: 400;
  line-height: 1.4;
  ${ellipsis}
`;

const Amounts = styled.div.attrs({ className: 'amounts' })`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const PrimaryAmount = styled.div<{ $credit: boolean; $visibility: 'show' | 'hide' | 'invisible' }>`
  font-size: var(--font-size);
  font-weight: 600;
  line-height: 1.4;
  ${({ $credit }) => ($credit ? `color: ${TX_FONT_CREDIT};` : '')}
  ${({ $visibility }) =>
    $visibility === 'hide'
      ? 'display: none;'
      : $visibility === 'invisible'
        ? 'visibility: hidden;'
        : ''}
`;

const SecondaryAmount = styled.div<{ $invisible?: boolean }>`
  font-size: var(--sub-font-size);
  font-weight: 400;
  line-height: 1.4;
  ${({ $invisible }) => ($invisible ? 'visibility: hidden;' : '')}
`;

const StatusBadge = styled.div`
  display: flex;
  height: 20px;
  padding: 2px 8px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  background: ${TX_FAILED_BADGE_BG};
  color: ${TX_FONT_FAILED};
`;

const OverlayLink = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
`;

const noteText = (note: Transaction['note'], locale: Locale): string => {
  if (!note) return '';
  if (typeof note === 'string') return note;
  return note[locale] ?? Object.values(note)[0] ?? '';
};

type Props = {
  tx: Transaction;
  last: boolean;
  locale: Locale;
  onNavigate: (route: string) => void;
};

export const TransactionRow: React.FC<Props> = ({ tx, last, locale, onNavigate }) => {
  const sign = tx.isCredit ? '+' : '-';
  const showFx = tx.isFinancial;
  const showFailedBadge = !tx.isFinancial && tx.status === 'FAILED';

  // Non-financial rows hide the primary amount (FAILED → drop it; otherwise
  // keep its space). Financial rows always show it.
  const primaryVisibility = tx.isFinancial
    ? 'show'
    : tx.status === 'FAILED'
      ? 'hide'
      : 'invisible';

  return (
    <Root
      $tone={toneOf(tx)}
      $strike={tx.isFinancial && tx.status === 'FAILED'}
      $last={last}
    >
      <IconBox $nonFinancial={!tx.isFinancial}>
        {tx.isFinancial ? <FinancialSymbol /> : <NonFinancialSymbol aria-hidden />}
      </IconBox>

      <Details>
        <Vendor>{tx.merchantName}</Vendor>
        <CardLine>
          **** {lastFour(tx.cardDetails.truncatedPan)}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {tx.cardDetails.aliasName}
        </CardLine>
      </Details>

      <Amounts>
        <PrimaryAmount $credit={tx.isCredit} $visibility={primaryVisibility}>
          {sign} {tx.formattedBaseAmount} {tx.baseCurrency}
        </PrimaryAmount>

        {showFailedBadge && (
          <StatusBadge>
            <Trans>FAILED</Trans>
          </StatusBadge>
        )}

        {showFx && (
          <SecondaryAmount $invisible={tx.baseCurrency === tx.currency}>
            {sign} {tx.formattedAmount} {tx.currency}
          </SecondaryAmount>
        )}

        {!tx.isFinancial && <SecondaryAmount>{noteText(tx.note, locale)}</SecondaryAmount>}
      </Amounts>

      {tx.url && (
        <OverlayLink
          type="button"
          aria-label={tx.merchantName}
          onClick={() => onNavigate(tx.url as string)}
        />
      )}
    </Root>
  );
};
